import React, { createContext, useState, useContext, useEffect, useRef } from 'react';
import { useAuth } from './AuthContext';
import * as dbService from '../services/dbService';
import { supabase } from '../lib/supabaseClient';
import { generateNextResponse, generateSuggestions, generateChapterSummary, generateChapterTitle } from '../services/aiService';

const StoryContext = createContext();

const defaultState = {
  storyTitle: 'Untitled Story',
  world: {
    genre: 'Fantasy',
    setting: '',
    pov: '3rd Person Limited',
    tone: 'Gritty & Dark',
    progress: 5,
    isPublic: false,
    backgroundImage: '',
    onboardingAnswers: {},
    worldEvents: [
      { id: 1, title: 'The Great Convergence', description: 'Celestial alignment begins.', status: 'Active', intensity: 80, timeTrigger: 'Turn 5', type: 'World' }
    ]
  },
  character: {
    name: '',
    nickname: '',
    preferredReference: 'name',
    role: '',
    gender: '',
    appearance: '',
    avatar: '',
    backstory: '',
    traits: ['Brave'],
    equipment: ['Leather Armor'],
    npcInteractions: [],
    stats: { level: 1, health: 100, mana: 50, strength: 10, agility: 10, intelligence: 10, experience: 0 }
  },
  quests: [
    { id: 1, title: 'The Blue Flame', description: 'Find the source of the azure light.', status: 'Active', reward: 'Enchanted Compass', difficulty: 'Medium', type: 'Main' }
  ],
  combat: {
    inCombat: false,
    enemy: null,
    combatLog: [],
    turnOrder: []
  },
  archive: [],
  onlineUsers: [],
  ai: {
    model: 'minimax/minimax-m2-her',
    provider: 'openrouter',
    apiKey: '',
    isMock: true,
    suggestions: ['Investigate the strange noise', 'Consult the local elder'],
    settings: {
      isPaused: localStorage.getItem('weaver_ai_paused') === 'true',
      creativity: 0.7,
      verbosity: 'Balanced',
      focus: 'Character-Centric',
      tone: 'Dramatic',
      vocabulary: 'Literary',
      responseLength: 'Medium',
      showEmotions: true,
      useDialogue: true,
      descriptionDepth: 0.6,
    },
    imageGenerator: {
      enabled: false,
      style: 'realistic',
    }
  },
  story: [],
  gallery: [],
  summary: 'Your journey is just beginning...',
  narrativeStyle: 'writer',
  isGenerating: false,
  analytics: {
    totalWords: 0,
    totalEntries: 0,
    sessionsCount: 0,
    genreBreakdown: {},
    activityLog: [],
    questsCompleted: 0,
    chaptersArchived: 0,
  },
  collaborators: [],
  storyIdeas: [],
  subscriptionTier: 'Wanderer',
  supabaseStoryId: null,
};

export const StoryProvider = ({ children }) => {
  const { user } = useAuth();
  const [state, setState] = useState(defaultState);
  const isGeneratingRef = useRef(false);
  const initPromiseRef = useRef(null);

  useEffect(() => {
    const loadUserProfile = async () => {
      if (user) {
        try {
          const profile = await dbService.getUserProfile(user.id);
          if (profile && profile.subscription_tier) {
            setSubscriptionTier(profile.subscription_tier);
          }
        } catch (err) {
          console.error("Failed to load user profile", err);
        }
      }
    };
    loadUserProfile();
  }, [user]);

  useEffect(() => {
    const loadUserStory = async () => {
      // Prevent loading the user's personal story if they are navigating directly to a shared collaboration link
      if (window.location.pathname.startsWith('/collab/')) {
        return;
      }

      // Only attempt to load if logged in and doesn't already have a synced story
      if (user && !state.supabaseStoryId) {
        try {
          let recentStory = null;
          const lastStoryId = localStorage.getItem('weaver_lastStoryId');
          if (lastStoryId) {
            recentStory = await dbService.getStoryById(lastStoryId).catch(() => null);
          }
          if (!recentStory) {
            recentStory = await dbService.getMostRecentStoryForUser(user.id);
          }
          if (recentStory) {
            const [entriesData, questsData, chaptersData, galleryData, collabsData] = await Promise.all([
              dbService.getStoryEntries(recentStory.id),
              dbService.getQuests(recentStory.id),
              dbService.getChapters(recentStory.id),
              dbService.getGalleryImages(recentStory.id),
              dbService.getCollaborators(recentStory.id)
            ]);

            setState(prev => ({
              ...prev,
              supabaseStoryId: recentStory.id,
              storyTitle: recentStory.title || 'Untitled Story',
              ownerId: recentStory.owner_id,
              world: {
                ...prev.world,
                genre: recentStory.world_genre || 'Fantasy',
                setting: recentStory.world_setting || '',
                pov: recentStory.world_pov || '3rd Person Limited',
                tone: recentStory.world_tone || 'Gritty & Dark',
                backgroundImage: recentStory.background_image || ''
              },
              character: {
                ...prev.character,
                name: recentStory.character_name || '',
                nickname: recentStory.character_nickname || '',
                preferredReference: recentStory.character_preferred_reference || 'name',
                role: recentStory.character_role || '',
                avatar: recentStory.character_avatar || '',
                backstory: recentStory.character_backstory || ''
              },
              ai: {
                ...prev.ai,
                model: recentStory.ai_model || prev.ai.model,
                imageGenerator: {
                  ...prev.ai.imageGenerator,
                  enabled: recentStory.ai_image_enabled !== null ? recentStory.ai_image_enabled : prev.ai.imageGenerator.enabled,
                  style: recentStory.ai_image_style || prev.ai.imageGenerator.style
                }
              },
              story: (entriesData || []).map(e => ({
                id: e.id,
                role: e.role,
                content: e.content,
                timestamp: e.created_at,
                character_name: e.character_name,
                character_nickname: e.character_nickname,
                character_preferred_reference: e.character_preferred_reference,
                user_id: e.user_id,
            profiles: e.profiles,
            bookmarked: e.bookmarked
          })),
              quests: questsData || [],
              archive: (chaptersData || []).map(c => ({
                ...c,
                timestamp: c.created_at,
                wordCount: c.word_count
              })),
              gallery: (galleryData || []).map(g => ({ ...g, timestamp: g.created_at })),
              collaborators: collabsData || []
            }));

            localStorage.setItem('weaver_lastStoryId', recentStory.id);

            // Recalculate analytics for loaded story
            setState(prev => {
              let totalEntries = prev.story.length;
              let totalWords = prev.story.reduce((acc, e) => acc + (e.content ? e.content.split(/\s+/).length : 0), 0);

              const activityMap = new Map();
              const addToMap = (timestamp, words) => {
                if (!timestamp) return;
                const date = new Date(timestamp).toISOString().split('T')[0];
                const existing = activityMap.get(date) || { date, words: 0, entries: 0 };
                activityMap.set(date, { date, words: existing.words + words, entries: existing.entries + 1 });
              };

              prev.story.forEach(e => {
                const words = e.content ? e.content.split(/\s+/).length : 0;
                addToMap(e.timestamp, words);
              });

              prev.archive.forEach(ch => {
                totalEntries += ch.content ? ch.content.length : 0;
                totalWords += ch.wordCount || (ch.content ? ch.content.reduce((acc, e) => acc + (e.content ? e.content.split(/\s+/).length : 0), 0) : 0);
                if (ch.content) {
                  ch.content.forEach(e => {
                    const words = e.content ? e.content.split(/\s+/).length : 0;
                    addToMap(e.timestamp, words);
                  });
                }
              });

              const activityLog = Array.from(activityMap.values()).sort((a, b) => new Date(a.date) - new Date(b.date));

              return {
                ...prev,
                analytics: {
                  ...prev.analytics,
                  totalEntries,
                  totalWords,
                  chaptersArchived: prev.archive.length,
                  activityLog,
                }
              };
            });
          }
        } catch (err) {
          console.error("Failed to load user's most recent story from DB", err);
        }
      }
    };
    loadUserStory();
  }, [user, state.subscriptionTier]);

  const loadStoryById = async (storyId) => {
    if (!user) return false;
    try {
      const recentStory = await dbService.getStoryById(storyId);
      if (recentStory) {
        localStorage.setItem('weaver_lastStoryId', recentStory.id);

        const [entriesData, questsData, chaptersData, galleryData, collabsData, ideasData] = await Promise.all([
          dbService.getStoryEntries(recentStory.id),
          dbService.getQuests(recentStory.id),
          dbService.getChapters(recentStory.id),
          dbService.getGalleryImages(recentStory.id),
          dbService.getCollaborators(recentStory.id),
          dbService.getStoryIdeas(user.id)
        ]);

        setState(prev => ({
          ...prev,
          supabaseStoryId: recentStory.id,
          storyTitle: recentStory.title || 'Untitled Story',
          ownerId: recentStory.owner_id,
          world: {
            ...prev.world,
            genre: recentStory.world_genre || 'Fantasy',
            setting: recentStory.world_setting || '',
            backgroundImage: recentStory.background_image || '',
            pov: recentStory.world_pov || '3rd Person Limited',
            tone: recentStory.world_tone || 'Gritty & Dark'
          },
          character: {
            ...prev.character,
            name: recentStory.character_name || '',
            nickname: recentStory.character_nickname || '',
            preferredReference: recentStory.character_preferred_reference || 'name',
            role: recentStory.character_role || '',
            avatar: recentStory.character_avatar || '',
            backstory: recentStory.character_backstory || ''
          },
          ai: {
            ...prev.ai,
            model: recentStory.ai_model || prev.ai.model,
            imageGenerator: {
              ...prev.ai.imageGenerator,
              enabled: recentStory.ai_image_enabled !== null ? recentStory.ai_image_enabled : prev.ai.imageGenerator.enabled,
              style: recentStory.ai_image_style || prev.ai.imageGenerator.style
            }
          },
          story: (entriesData || []).map(e => ({
            id: e.id,
            role: e.role,
            content: e.content,
            timestamp: e.created_at,
            character_name: e.character_name,
            character_nickname: e.character_nickname,
            character_preferred_reference: e.character_preferred_reference,
            user_id: e.user_id,
            profiles: e.profiles,
            bookmarked: e.bookmarked
          })),
          quests: questsData || [],
          archive: (chaptersData || []).map(c => ({
            ...c,
            timestamp: c.created_at,
            wordCount: c.word_count
          })),
          gallery: (galleryData || []).map(g => ({ ...g, timestamp: g.created_at })),
          collaborators: collabsData || [],
          storyIdeas: ideasData || []
        }));

        // Recalculate analytics for loaded story
        setState(prev => {
          let totalEntries = prev.story.length;
          let totalWords = prev.story.reduce((acc, e) => acc + (e.content ? e.content.split(/\s+/).length : 0), 0);

          const activityMap = new Map();
          const addToMap = (timestamp, words) => {
            if (!timestamp) return;
            const date = new Date(timestamp).toISOString().split('T')[0];
            const existing = activityMap.get(date) || { date, words: 0, entries: 0 };
            activityMap.set(date, { date, words: existing.words + words, entries: existing.entries + 1 });
          };

          prev.story.forEach(e => {
            const words = e.content ? e.content.split(/\s+/).length : 0;
            addToMap(e.timestamp, words);
          });

          prev.archive.forEach(ch => {
            totalEntries += ch.content ? ch.content.length : 0;
            totalWords += ch.wordCount || (ch.content ? ch.content.reduce((acc, e) => acc + (e.content ? e.content.split(/\s+/).length : 0), 0) : 0);
            if (ch.content) {
              ch.content.forEach(e => {
                const words = e.content ? e.content.split(/\s+/).length : 0;
                addToMap(e.timestamp, words);
              });
            }
          });

          const activityLog = Array.from(activityMap.values()).sort((a, b) => new Date(a.date) - new Date(b.date));

          return {
            ...prev,
            analytics: {
              ...prev.analytics,
              totalEntries,
              totalWords,
              chaptersArchived: prev.archive.length,
              activityLog,
            }
          };
        });

        return true;
      }
    } catch (err) {
      console.error("Failed to load story by ID", err);
    }
    return false;
  };

  // Real-time synchronization
  useEffect(() => {
    if (!state.supabaseStoryId || !user) return;

    const channel = supabase.channel(`story_entries_${state.supabaseStoryId}`, {
      config: { presence: { key: user.id } }
    })
      .on('presence', { event: 'sync' }, () => {
        const presenceState = channel.presenceState();
        const activeUsers = Object.keys(presenceState);
        setState(prev => ({ ...prev, onlineUsers: activeUsers }));
      })
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'story_entries', filter: `story_id=eq.${state.supabaseStoryId}` },
        (payload) => {
          if (payload.eventType === 'INSERT') {
            const newEntry = {
              id: payload.new.id,
              role: payload.new.role,
              content: payload.new.content,
              timestamp: payload.new.created_at,
              character_name: payload.new.character_name,
              character_nickname: payload.new.character_nickname,
              character_preferred_reference: payload.new.character_preferred_reference,
                user_id: payload.new.user_id,
                bookmarked: payload.new.bookmarked
            };

            setState(prev => {
              // Check if we already have this entry optimistically added
              const existingIdx = prev.story.findIndex(e => e.id === newEntry.id || (e.content === newEntry.content && e.role === newEntry.role));

              if (existingIdx !== -1) {
                  const existingEntry = prev.story[existingIdx];
                  // If it has a temp ID, we upgrade it
                  if (typeof existingEntry.id === 'string' && existingEntry.id.startsWith('temp_')) {
                      const newStory = [...prev.story];
                      newStory[existingIdx] = { ...existingEntry, ...newEntry };
                      return { ...prev, story: newStory };
                  }
                  // Otherwise it's a true duplicate (e.g. strict match), so ignore
                  return prev;
              }

              const words = (newEntry.content || '').trim().split(/\s+/).filter(Boolean).length;
              const today = new Date().toISOString().split('T')[0];
              const prevAnalytics = prev.analytics || {};
              const log = [...(prevAnalytics.activityLog || [])];
              const todayIdx = log.findIndex(l => l.date === today);
              if (todayIdx >= 0) {
                log[todayIdx] = { ...log[todayIdx], words: log[todayIdx].words + words, entries: log[todayIdx].entries + 1 };
              } else {
                log.push({ date: today, words, entries: 1 });
              }

              return {
                ...prev,
                story: [...prev.story, newEntry],
                analytics: {
                  ...prevAnalytics,
                  totalWords: (prevAnalytics.totalWords || 0) + words,
                  totalEntries: (prevAnalytics.totalEntries || 0) + 1,
                  activityLog: log.slice(-30),
                }
              };
            });
          } else if (payload.eventType === 'UPDATE') {
            setState(prev => ({
              ...prev,
              story: prev.story.map(e => e.id === payload.new.id ? { ...e, content: payload.new.content, bookmarked: payload.new.bookmarked } : e)
            }));
          } else if (payload.eventType === 'DELETE') {
            setState(prev => ({
              ...prev,
              story: prev.story.filter(e => e.id !== payload.old.id)
            }));
          }
        }
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'gallery_images', filter: `story_id=eq.${state.supabaseStoryId}` },
        (payload) => {
          if (payload.eventType === 'INSERT') {
             setState(prev => {
               const existingIdx = (prev.gallery || []).findIndex(img => img.id === payload.new.id || (img.url === payload.new.url && img.baseScene === payload.new.base_scene));

               const newImage = {
                 id: payload.new.id,
                 url: payload.new.url,
                 baseScene: payload.new.base_scene,
                 modifications: payload.new.modifications,
                 history: payload.new.history,
                 timestamp: payload.new.created_at
               };

               if (existingIdx !== -1) {
                 const existingImage = prev.gallery[existingIdx];
                 // If the existing image has a temporary ID (no hyphens), upgrade it in place.
                 // Otherwise, we consider it a duplicate and ignore.
                 if (typeof existingImage.id === 'number' || (typeof existingImage.id === 'string' && !existingImage.id.includes('-'))) {
                    const newGallery = [...prev.gallery];
                    newGallery[existingIdx] = newImage;
                    return { ...prev, gallery: newGallery };
                 }
                 return prev;
               }

               return { ...prev, gallery: [newImage, ...(prev.gallery || [])]};
             });
          } else if (payload.eventType === 'UPDATE') {
             setState(prev => ({ ...prev, gallery: (prev.gallery || []).map(img => img.id === payload.new.id ? { ...img, url: payload.new.url, baseScene: payload.new.base_scene, modifications: payload.new.modifications, history: payload.new.history } : img) }));
          } else if (payload.eventType === 'DELETE') {
             setState(prev => ({ ...prev, gallery: (prev.gallery || []).filter(img => img.id !== payload.old.id) }));
          }
        }
      )
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'stories', filter: `id=eq.${state.supabaseStoryId}` },
        (payload) => {
          setState(prev => ({
            ...prev,
            world: {
              ...prev.world,
              genre: payload.new.world_genre !== undefined ? payload.new.world_genre : prev.world.genre,
              setting: payload.new.world_setting !== undefined ? payload.new.world_setting : prev.world.setting,
              pov: payload.new.world_pov !== undefined ? payload.new.world_pov : prev.world.pov,
              tone: payload.new.world_tone !== undefined ? payload.new.world_tone : prev.world.tone,
              backgroundImage: payload.new.background_image !== undefined ? payload.new.background_image : prev.world.backgroundImage
            },
            storyTitle: payload.new.title !== undefined ? payload.new.title : prev.storyTitle,
            character: {
              ...prev.character,
              name: payload.new.character_name !== undefined ? payload.new.character_name : prev.character.name,
              nickname: payload.new.character_nickname !== undefined ? payload.new.character_nickname : prev.character.nickname,
              preferredReference: payload.new.character_preferred_reference !== undefined ? payload.new.character_preferred_reference : prev.character.preferredReference,
              role: payload.new.character_role !== undefined ? payload.new.character_role : prev.character.role,
              avatar: payload.new.character_avatar !== undefined ? payload.new.character_avatar : prev.character.avatar,
              backstory: payload.new.character_backstory !== undefined ? payload.new.character_backstory : prev.character.backstory
            }
          }));
        }
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'collaborators', filter: `story_id=eq.${state.supabaseStoryId}` },
        async () => {
          try {
            const collabsData = await dbService.getCollaborators(state.supabaseStoryId);
            setState(prev => ({ ...prev, collaborators: collabsData || [] }));
          } catch (e) {
            console.error('Failed to sync collaborators:', e);
          }
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          channel.track({ online_at: new Date().toISOString() });
        }
      });

    return () => {
      supabase.removeChannel(channel);
    };
  }, [state.supabaseStoryId, user]);

  const updateWorld = (worldData) => {
    setState(prev => {
      const updatedData = typeof worldData === 'function' ? worldData(prev.world) : worldData;
      const newState = { ...prev, world: { ...prev.world, ...updatedData } };
      // If the story exists in the backend, anyone collaborating can update it
      if (newState.supabaseStoryId && user) {
        dbService.updateStoryMetadata(newState.supabaseStoryId, newState).catch(console.error);
      }
      return newState;
    });
  };

  const startCombat = (enemy) => {
    setState(prev => ({
      ...prev,
      combat: {
        inCombat: true,
        enemy,
        combatLog: [`Encounter started with ${enemy.name}!`],
        turnOrder: ['player', 'enemy'] // Simple implementation for now
      }
    }));
  };

  const endCombat = () => {
    setState(prev => ({
      ...prev,
      combat: {
        inCombat: false,
        enemy: null,
        combatLog: [],
        turnOrder: []
      }
    }));
  };

  const addCombatLog = (message) => {
    setState(prev => ({
      ...prev,
      combat: {
        ...prev.combat,
        combatLog: [...prev.combat.combatLog, message]
      }
    }));
  };

  const toggleAiPause = () => {
    setState(prev => {
      let isCurrentlyPaused = prev.ai.settings.isPaused;

      // Update backend if it's a collaborator
      if (user && prev.supabaseStoryId && prev.collaborators) {
         const userCollab = prev.collaborators.find(c => c.user_id === user.id);
         if (userCollab) {
             isCurrentlyPaused = userCollab.ai_paused !== undefined ? userCollab.ai_paused : prev.ai.settings.isPaused;
             const newPausedState = !isCurrentlyPaused;
             localStorage.setItem('weaver_ai_paused', newPausedState);

             // Let's update the local collaborators state too
             dbService.updateCollaboratorAiPaused(prev.supabaseStoryId, user.id, newPausedState).catch(console.error);
             const updatedCollaborators = prev.collaborators.map(c =>
                 c.user_id === user.id ? { ...c, ai_paused: newPausedState } : c
             );
             return {
               ...prev,
               collaborators: updatedCollaborators
               // We don't overwrite the global AI pause setting if we are just a collaborator
             };
         }
      }

      const newPausedState = !isCurrentlyPaused;
      localStorage.setItem('weaver_ai_paused', newPausedState);
      const newState = {
        ...prev,
        ai: { ...prev.ai, settings: { ...prev.ai.settings, isPaused: newPausedState } }
      };

      // Persist the state if we are the owner
      if (prev.supabaseStoryId && user) {
          dbService.updateStoryMetadata(prev.supabaseStoryId, newState).catch(console.error);
      }

      return newState;
    });
  };

  const sendPoke = async (targetUserId) => {
    if (!user) return false;
    try {
      const senderName = user.user_metadata?.username || user.email?.split('@')[0] || 'A collaborator';
      const storyTitle = state.storyTitle || state.title || 'Current Story';
      await dbService.sendPokeDB(targetUserId, user.id, senderName, storyTitle, state.supabaseStoryId);
      return true;
    } catch (error) {
      console.error('Failed to send poke:', error);
      return false;
    }
  };

  const updateStoryTitle = (title) => {
    setState(prev => {
      if (prev.supabaseStoryId && user) {
        dbService.updateStoryTitleDB(prev.supabaseStoryId, title).catch(console.error);
      }
      return { ...prev, storyTitle: title };
    });
  };

  const updateCharacter = (charData) => {
    setState(prev => {
      const newState = { ...prev, character: { ...prev.character, ...charData } };
      if (newState.supabaseStoryId && user) {
        dbService.updateStoryMetadata(newState.supabaseStoryId, newState).catch(console.error);
      }
      return newState;
    });
  };

  const initializeStoryBackend = async () => {
    if (state.supabaseStoryId || !user) return state.supabaseStoryId;
    if (initPromiseRef.current) return initPromiseRef.current;

    initPromiseRef.current = (async () => {
      try {
        const charName = state.character?.name?.trim();
        const title = charName ? `${charName}'s Tale` : 'New Story';
        const newStory = await dbService.createStory(user.id, title, state);
        setState(prev => ({ ...prev, supabaseStoryId: newStory.id }));

        // Upload any existing local entries to the backend and update their IDs
        if (state.story && state.story.length > 0) {
          const updatedEntries = [];
          for (let i = 0; i < state.story.length; i++) {
             const savedEntry = await dbService.saveStoryEntry(newStory.id, state.story[i], i);
             updatedEntries.push({ ...state.story[i], id: savedEntry.id });
          }
          setState(prev => {
             const newStory = [...prev.story];
             for (let i = 0; i < updatedEntries.length; i++) {
               if (newStory[i]) {
                 newStory[i] = { ...newStory[i], id: updatedEntries[i].id };
               }
             }
             return { ...prev, story: newStory };
          });
        }

        return newStory.id;
      } catch (err) {
        console.error("Failed to initialize story in db:", err);
        return null;
      } finally {
        initPromiseRef.current = null;
      }
    })();

    return initPromiseRef.current;
  };

  const addStoryEntry = async (entry) => {
    // Generate tempId using a reliable uuid or larger timestamp plus random string to avoid duplicate keys on fast sends
    const tempId = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    // Capture the synchronous state
    const currentLength = state.story.length;
    const currentArchiveLength = state.archive.length;
    const currentWorld = { ...state.world };

    // Optimistically update the UI first
    setState(prev => {
      const words = (entry.content || '').trim().split(/\s+/).filter(Boolean).length;
      const today = new Date().toISOString().split('T')[0];
      const prevAnalytics = prev.analytics || {};
      const log = [...(prevAnalytics.activityLog || [])];
      const todayIdx = log.findIndex(l => l.date === today);
      if (todayIdx >= 0) {
        log[todayIdx] = { ...log[todayIdx], words: log[todayIdx].words + words, entries: log[todayIdx].entries + 1 };
      } else {
        log.push({ date: today, words, entries: 1 });
      }
      return {
        ...prev,
          story: [...prev.story, { ...entry, id: tempId }],
        analytics: {
          ...prevAnalytics,
          totalWords: (prevAnalytics.totalWords || 0) + words,
          totalEntries: (prevAnalytics.totalEntries || 0) + 1,
          activityLog: log.slice(-30),
        }
      };
    });

    // Then handle the database persistence
    let currentStoryId = state.supabaseStoryId;
    if (!currentStoryId && user) {
      currentStoryId = await initializeStoryBackend();
    }

    if (currentStoryId && user) {
      try {
        const savedEntry = await dbService.saveStoryEntry(currentStoryId, entry, currentLength);
        // Replace tempId with the real backend UUID
        setState(prev => ({
          ...prev,
          story: prev.story.map(e => e.id === tempId ? { ...e, id: savedEntry.id } : e)
        }));

        // Auto-create initial chapter if this is the first entry
        if (currentLength === 0 && currentArchiveLength === 0) {
          const firstEntry = { id: savedEntry.id, ...entry };
          const contentString = firstEntry.content || '';
          const newChapter = {
            id: Date.now(),
            title: "Chapter 1",
            content: [firstEntry],
            summary: "The journey begins.",
            timestamp: new Date().toISOString(),
            worldSnapshot: currentWorld,
            wordCount: contentString.trim().split(/\s+/).length,
          };
          setState(prev => ({
            ...prev,
            archive: [newChapter, ...prev.archive],
            analytics: {
              ...prev.analytics,
              chaptersArchived: (prev.analytics.chaptersArchived || 0) + 1,
            }
          }));

          if (currentStoryId && user) {
            try {
              const savedChapter = await dbService.saveChapter(currentStoryId, newChapter);
              setState(prev => ({
                ...prev,
                archive: prev.archive.map(c => c.id === newChapter.id ? { ...c, id: savedChapter.id } : c)
              }));
            } catch (err) { console.error('Failed to auto-save first chapter', err); }
          }
        }
      } catch (err) {
        console.error("Failed to save entry to db:", err);
      }
    }
  };

  const submitPassage = async (content, narrativeStyle) => {
    if (!content.trim() || isGeneratingRef.current) return;

    setGenerating(true);

    try {
      // Determine the active character for the current user
      let activeCharacter = state.character;

      // If the user is not the owner (is a collaborator), default to null so they don't post as the owner's character
      if (user && state.ownerId && user.id !== state.ownerId) {
          activeCharacter = null;
      }

      // If the user is a collaborator (not the owner), use their mapped character
      if (user && state.ownerId && user.id !== state.ownerId && state.collaborators) {
         const userCollab = state.collaborators.find(c => c.user_id === user.id);
         if (userCollab && userCollab.characters) {
             activeCharacter = {
                 name: userCollab.characters.name,
                 nickname: userCollab.characters.nickname,
                 preferredReference: userCollab.characters.preferred_reference || 'name'
             };
         }
      }

      // Add user entry first
      const userEntry = {
        role: 'user',
        content,
        timestamp: new Date().toISOString(),
        user_id: user?.id,
        character_name: activeCharacter?.name,
        character_nickname: activeCharacter?.nickname,
        character_preferred_reference: activeCharacter?.preferredReference || activeCharacter?.preferred_reference || 'name'
      };
      await addStoryEntry(userEntry);

      // Determine if AI is paused for this specific user
      let isAiPausedForUser = state.ai.settings.isPaused;
      if (user && state.collaborators) {
         const userCollab = state.collaborators.find(c => c.user_id === user.id);
         if (userCollab && userCollab.ai_paused !== undefined) {
             isAiPausedForUser = userCollab.ai_paused;
         }
      }

      if (!isAiPausedForUser) {
        // Collect all active characters for the AI prompt
        const activeCharacters = [state.character]; // Main character

        if (state.collaborators) {
           state.collaborators.forEach(c => {
               if (c.characters && c.status === 'Accepted') {
                  activeCharacters.push({
                      name: c.characters.name,
                      nickname: c.characters.nickname,
                      preferredReference: c.characters.preferred_reference || 'name',
                      stats: c.characters.stats
                  });
               }
           });
        }

        // Use the updated story context by resolving state.story instead of capturing old state
        // We pass the updated history directly
        const updatedHistory = [...state.story, userEntry];

        await new Promise(resolve => setTimeout(resolve, 800));

        let aiResponse = await generateNextResponse(content, state.world, activeCharacters, updatedHistory, narrativeStyle, state.ai.settings, state.ai.apiKey, state.ai.model);

        // Parse for COMBAT_START tag
        const combatMatch = aiResponse.match(/\[COMBAT_START:\s*([^|\]]+)\|?([^|\]]+)?\|?([^\]]+)?\]/);
        if (combatMatch) {
            const enemyName = combatMatch[1]?.trim() || 'Enemy';
            const enemyLevel = parseInt(combatMatch[2]) || 1;
            const enemyHp = parseInt(combatMatch[3]) || 50;

            // Remove the tag from the final story text
            aiResponse = aiResponse.replace(/\[COMBAT_START:.*?\]/g, '').trim();

            // Start combat overlay
            startCombat({
                name: enemyName,
                stats: { level: enemyLevel, health: enemyHp, maxHealth: enemyHp }
            });
        }

        await addStoryEntry({ role: 'ai', content: aiResponse, timestamp: new Date().toISOString() });

        if (narrativeStyle !== 'writer') {
          const historyForSuggestions = [...updatedHistory, { role: 'ai', content: aiResponse }];
          const newSuggestions = await generateSuggestions(historyForSuggestions, state.world, activeCharacters, state.ai.apiKey, state.ai.model);
          setSuggestions(newSuggestions);
        }
      }

      updateWorld(prevWorld => ({ progress: Math.min(100, prevWorld.progress + 3) }));
    } finally {
      setGenerating(false);
    }
  };

  const editStoryEntry = (id, newContent) => {
    setState(prev => ({
      ...prev,
      story: prev.story.map(entry => entry.id === id ? { ...entry, content: newContent } : entry)
    }));

    if (state.supabaseStoryId && user) {
      dbService.updateStoryEntry(id, newContent).catch(console.error);
    }
  };

  const toggleEntryBookmark = (id, bookmarked) => {
    setState(prev => ({
      ...prev,
      story: prev.story.map(entry => entry.id === id ? { ...entry, bookmarked } : entry)
    }));

    if (state.supabaseStoryId && user) {
      dbService.toggleStoryEntryBookmark(id, bookmarked).catch(console.error);
    }
  };

  const editArchivedEntry = (chapterId, entryId, newContent) => {
    const chapter = state.archive.find(c => c.id === chapterId);
    if (!chapter) return;
    const newChapterContent = chapter.content.map(e => e.id === entryId ? { ...e, content: newContent } : e);

    setState(prev => ({
      ...prev,
      archive: prev.archive.map(c => c.id === chapterId ? { ...c, content: newChapterContent } : c)
    }));

    if (state.supabaseStoryId && user) {
      dbService.updateChapterContentDB(chapterId, newChapterContent).catch(console.error);
    }
  };

  const toggleArchivedEntryBookmark = (chapterId, entryId, bookmarked) => {
    const chapter = state.archive.find(c => c.id === chapterId);
    if (!chapter) return;
    const newChapterContent = chapter.content.map(e => e.id === entryId ? { ...e, bookmarked } : e);

    setState(prev => ({
      ...prev,
      archive: prev.archive.map(c => c.id === chapterId ? { ...c, content: newChapterContent } : c)
    }));

    if (state.supabaseStoryId && user) {
      dbService.updateChapterContentDB(chapterId, newChapterContent).catch(console.error);
    }
  };

  const toggleChapterBookmark = (chapterId, bookmarked) => {
    setState(prev => ({
      ...prev,
      archive: prev.archive.map(c => c.id === chapterId ? { ...c, bookmarked } : c)
    }));

    if (state.supabaseStoryId && user) {
      dbService.toggleChapterBookmarkDB(chapterId, bookmarked).catch(console.error);
    }
  };

  const logDeletion = (type, item) => {
    try {
      const logs = JSON.parse(localStorage.getItem('weaver_deletion_logs') || '[]');
      logs.unshift({ type, item, deletedAt: new Date().toISOString() });
      if (logs.length > 50) logs.pop();
      localStorage.setItem('weaver_deletion_logs', JSON.stringify(logs));
    } catch (e) {
      console.error('Failed to log deletion', e);
    }
  };

  const deleteArchivedEntry = (chapterId, entryId) => {
    const chapter = state.archive.find(c => c.id === chapterId);
    if (!chapter) return;

    const entryToDelete = chapter.content.find(e => e.id === entryId);
    if (entryToDelete) logDeletion('archived_entry', entryToDelete);

    const newChapterContent = chapter.content.filter(e => e.id !== entryId);

    setState(prev => ({
      ...prev,
      archive: prev.archive.map(c => c.id === chapterId ? { ...c, content: newChapterContent } : c)
    }));

    if (state.supabaseStoryId && user) {
      dbService.updateChapterContentDB(chapterId, newChapterContent).catch(console.error);
    }
  };

  const deleteStoryEntry = (id) => {
    const entryToDelete = state.story.find(e => e.id === id);
    if (entryToDelete) logDeletion('story_entry', entryToDelete);

    setState(prev => ({
      ...prev,
      story: prev.story.filter(entry => entry.id !== id)
    }));

    if (state.supabaseStoryId && user) {
      dbService.deleteStoryEntryDB(id).catch(console.error);
    }
  };

  const startOverChapter = () => {
    if (state.story.length > 0) logDeletion('chapter_clear', [...state.story]);
    setState(prev => ({ ...prev, story: [] }));
    if (state.supabaseStoryId && user) {
      dbService.deleteAllStoryEntriesDB(state.supabaseStoryId).catch(console.error);
    }
  };

  const startOverStory = () => {
    logDeletion('story_reset', { ...state });
    setState({ ...defaultState });
  };

  const importStoryState = async (newState) => {
    // Determine the imported relational arrays
    const importedEntries = newState.story || [];
    const importedArchive = newState.archive || [];
    const importedGallery = newState.gallery || [];
    const importedQuests = newState.quests || [];

    // Update the base state with imported preferences, world, character, etc.
    // but exclude the arrays we are about to manually append.
    const baseNewState = { ...newState };
    delete baseNewState.story;
    delete baseNewState.archive;
    delete baseNewState.gallery;
    delete baseNewState.quests;

    // Recalculate analytics to ensure consistency
    let totalEntries = importedEntries.length;
    let totalWords = importedEntries.reduce((acc, e) => acc + (e.content ? e.content.split(/\s+/).length : 0), 0);
    importedArchive.forEach(ch => {
      totalEntries += ch.content ? ch.content.length : 0;
      totalWords += ch.wordCount || (ch.content ? ch.content.reduce((acc, e) => acc + (e.content ? e.content.split(/\s+/).length : 0), 0) : 0);
    });

    baseNewState.analytics = {
      ...baseNewState.analytics,
      totalEntries,
      totalWords,
      sessionsCount: baseNewState.analytics?.sessionsCount || 1,
      activityLog: baseNewState.analytics?.activityLog || [],
      chaptersArchived: importedArchive.length
    };

    setState(prev => ({ ...prev, ...baseNewState }));

    let currentStoryId = state.supabaseStoryId;
    if (!currentStoryId && user) {
      currentStoryId = await initializeStoryBackend();
    }

    // Append stories
    if (importedEntries.length > 0) {
      if (currentStoryId && user) {
        try {
          const newSavedEntries = [];
          for (let i = 0; i < importedEntries.length; i++) {
             const entry = importedEntries[i];
             const savedEntry = await dbService.saveStoryEntry(currentStoryId, entry, state.story.length + i);
             newSavedEntries.push({ ...entry, id: savedEntry.id });
          }
          setState(prev => {
            const existingIds = new Set((prev.story || []).map(e => e.id));
            const uniqueNew = newSavedEntries.filter(e => !existingIds.has(e.id));
            return { ...prev, story: [...(prev.story || []), ...uniqueNew] };
          });
        } catch (err) {
          console.error("Failed to import and save entries to db:", err);
        }
      } else {
        setState(prev => {
            const existingIds = new Set((prev.story || []).map(e => e.id));
            const uniqueNew = importedEntries.filter(e => !existingIds.has(e.id));
            return { ...prev, story: [...(prev.story || []), ...uniqueNew] };
        });
      }
    }

    // Append archives
    if (importedArchive.length > 0) {
      if (currentStoryId && user) {
        try {
          const newSavedArchives = [];
          for (let i = 0; i < importedArchive.length; i++) {
             const chapter = importedArchive[i];
             const savedChapter = await dbService.saveChapter(currentStoryId, chapter);
             newSavedArchives.push({ ...chapter, id: savedChapter.id });
          }
          setState(prev => {
            const existingIds = new Set((prev.archive || []).map(a => a.id));
            const uniqueNew = newSavedArchives.filter(a => !existingIds.has(a.id));
            return { ...prev, archive: [...(prev.archive || []), ...uniqueNew] };
          });
        } catch (err) { console.error("Failed to import and save archives to db:", err); }
      } else {
        setState(prev => {
            const existingIds = new Set((prev.archive || []).map(a => a.id));
            const uniqueNew = importedArchive.filter(a => !existingIds.has(a.id));
            return { ...prev, archive: [...(prev.archive || []), ...uniqueNew] };
        });
      }
    }

    // Append gallery
    if (importedGallery.length > 0) {
      if (currentStoryId && user) {
        try {
          const newSavedGallery = [];
          for (let i = 0; i < importedGallery.length; i++) {
             const image = importedGallery[i];
             const savedImage = await dbService.saveGalleryImage(currentStoryId, image);
             newSavedGallery.push({ ...image, id: savedImage.id });
          }
          setState(prev => {
            const existingIds = new Set((prev.gallery || []).map(g => g.id));
            const uniqueNew = newSavedGallery.filter(g => !existingIds.has(g.id));
            return { ...prev, gallery: [...(prev.gallery || []), ...uniqueNew] };
          });
        } catch (err) { console.error("Failed to import and save gallery to db:", err); }
      } else {
        setState(prev => {
            const existingIds = new Set((prev.gallery || []).map(g => g.id));
            const uniqueNew = importedGallery.filter(g => !existingIds.has(g.id));
            return { ...prev, gallery: [...(prev.gallery || []), ...uniqueNew] };
        });
      }
    }

    // Append quests
    if (importedQuests.length > 0) {
      if (currentStoryId && user) {
        try {
          const newSavedQuests = [];
          for (let i = 0; i < importedQuests.length; i++) {
             const quest = importedQuests[i];
             const savedQuest = await dbService.saveQuest(currentStoryId, quest);
             newSavedQuests.push({ ...quest, id: savedQuest.id });
          }
          setState(prev => {
            const existingIds = new Set((prev.quests || []).map(q => q.id));
            const uniqueNew = newSavedQuests.filter(q => !existingIds.has(q.id));
            return { ...prev, quests: [...(prev.quests || []), ...uniqueNew] };
          });
        } catch (err) { console.error("Failed to import and save quests to db:", err); }
      } else {
        setState(prev => {
            const existingIds = new Set((prev.quests || []).map(q => q.id));
            const uniqueNew = importedQuests.filter(q => !existingIds.has(q.id));
            return { ...prev, quests: [...(prev.quests || []), ...uniqueNew] };
        });
      }
    }
  };

  const setGenerating = (isGen) => {
    isGeneratingRef.current = isGen;
    setState(prev => ({ ...prev, isGenerating: isGen }));
  };
  const setNarrativeStyle = (style) => setState(prev => ({ ...prev, narrativeStyle: style }));
  const updateAI = (aiData) => {
    setState(prev => {
      const newState = { ...prev, ai: { ...prev.ai, ...aiData } };
      if (newState.supabaseStoryId && user) {
        dbService.updateStoryMetadata(newState.supabaseStoryId, newState).catch(console.error);
      }
      return newState;
    });
  };
  const updateAISettings = (settings) => {
    setState(prev => {
      const newState = {
        ...prev,
        ai: { ...prev.ai, settings: { ...prev.ai.settings, ...settings } }
      };
      if (settings.isPaused !== undefined) {
        localStorage.setItem('weaver_ai_paused', settings.isPaused);
      }
      if (newState.supabaseStoryId && user) {
        dbService.updateStoryMetadata(newState.supabaseStoryId, newState).catch(console.error);
      }
      return newState;
    });
  };
  const setSuggestions = (suggestions) => setState(prev => ({ ...prev, ai: { ...prev.ai, suggestions } }));

  const archiveCurrentStory = async (title, chapterSummary = '') => {
    if (state.story.length === 0) return;

    // Auto-generate title and summary if none provided
    let finalSummary = chapterSummary;
    let finalTitle = title;

    if (!finalSummary) {
      finalSummary = await generateChapterSummary(state.story, state.ai.apiKey, state.ai.model);
    }

    if (!finalTitle) {
      finalTitle = await generateChapterTitle(finalSummary, state.ai.apiKey, state.ai.model);
    }

    const newChapter = {
      id: Date.now(),
      title: finalTitle || `Chapter ${state.archive.length + 1}`,
      content: [...state.story],
      summary: finalSummary,
      timestamp: new Date().toISOString(),
      worldSnapshot: { ...state.world },
      wordCount: state.story.reduce((acc, e) => acc + e.content.split(' ').length, 0),
    };
    setState(prev => ({
      ...prev,
      story: [],
      suggestions: [],
      archive: [newChapter, ...prev.archive],
      analytics: {
        ...prev.analytics,
        chaptersArchived: (prev.analytics.chaptersArchived || 0) + 1,
      }
    }));

    if (state.supabaseStoryId && user) {
      try {
        const savedChapter = await dbService.saveChapter(state.supabaseStoryId, newChapter);
        await dbService.deleteAllStoryEntriesDB(state.supabaseStoryId);

        setState(prev => ({
          ...prev,
          archive: prev.archive.map(c => c.id === newChapter.id ? { ...c, id: savedChapter.id } : c)
        }));
      } catch (err) { console.error('Failed to save chapter and clear entries', err); }
    }
  };

  const unarchiveChapter = async (chapterId) => {
    const chapterToUnarchive = state.archive.find(c => c.id === chapterId);
    if (!chapterToUnarchive) return;

    // Find the minimum order index in the current story to place restored entries before them
    let minOrderIndex = 0;
    if (state.story.length > 0 && state.story[0].order_index !== undefined) {
      minOrderIndex = state.story[0].order_index;
    }

    // Remove from archive and prepend contents to the active story
    setState(prev => ({
      ...prev,
      story: [...chapterToUnarchive.content, ...prev.story],
      archive: prev.archive.filter(c => c.id !== chapterId),
      analytics: {
        ...prev.analytics,
        chaptersArchived: Math.max(0, (prev.analytics.chaptersArchived || 1) - 1),
      }
    }));

    if (state.supabaseStoryId && user) {
      try {
        // Sequentially save restored entries back into the story_entries table
        // Use order indices lower than the current minimum so they appear at the start
        let currentOrderIndex = minOrderIndex - chapterToUnarchive.content.length;
        for (const entry of chapterToUnarchive.content) {
          await dbService.saveStoryEntry(state.supabaseStoryId, entry, currentOrderIndex++);
        }
        await dbService.deleteChapter(chapterId);
      } catch (err) { console.error('Failed to unarchive chapter and restore entries to db', err); }
    }
  };

  const addQuest = async (quest) => {
    const tempId = Date.now();
    const userId = quest.scope === 'Individual' ? user?.id : null;
    const newQuest = { id: tempId, status: 'Active', ...quest, user_id: userId };

    // Optimistic update
    setState(prev => ({ ...prev, quests: [newQuest, ...prev.quests] }));

    if (state.supabaseStoryId && user) {
      try {
        const savedQuest = await dbService.saveQuest(state.supabaseStoryId, newQuest, userId);
        // Replace temp id with real db id
        setState(prev => ({
          ...prev,
          quests: prev.quests.map(q => q.id === tempId ? { ...savedQuest } : q)
        }));
      } catch (err) {
        console.error("Failed to save quest to DB:", err);
      }
    }
  };

  const completeQuest = async (id) => {
    // Optimistic update
    setState(prev => ({
      ...prev,
      quests: prev.quests.map(q => q.id === id ? { ...q, status: 'Completed' } : q),
      analytics: { ...prev.analytics, questsCompleted: (prev.analytics.questsCompleted || 0) + 1 }
    }));

    if (state.supabaseStoryId && user) {
      try {
        await dbService.updateQuestDB(id, { status: 'Completed' });
      } catch (err) {
        console.error("Failed to update quest status in DB:", err);
      }
    }
  };

  const scheduleEvent = (event) => setState(prev => ({ ...prev, world: { ...prev.world, worldEvents: [event, ...prev.world.worldEvents] } }));

  const addCollaborator = async (collaborator, explicitStoryId = null) => {
    // Use explicitly passed story ID (to avoid stale state bugs in async flows)
    // or fallback to the state, or initialize a new one.
    let currentStoryId = explicitStoryId || state.supabaseStoryId;

    if (!currentStoryId && user) {
      currentStoryId = await initializeStoryBackend();
    }

    // Try to invite the user via DB service using their username (collaborator.name)
    if (currentStoryId && collaborator.name) {
      try {
        await dbService.addCollaborator(currentStoryId, collaborator.name, collaborator.role, null, state.ai?.settings?.isPaused || false);
        // Only update local state if DB update succeeds
        setState(prev => ({ ...prev, collaborators: [...prev.collaborators, { id: Date.now(), ...collaborator, ai_paused: prev.ai?.settings?.isPaused || false }] }));
      } catch (err) {
        console.error("Failed to add collaborator to db:", err);
        // Fallback for when the user is not found in the DB.
        // We still allow adding them locally so they can be invited via email/link.
        // We will store them in local state but they won't be saved to DB.
        setState(prev => ({ ...prev, collaborators: [...prev.collaborators, { id: Date.now(), ...collaborator, isPending: true, ai_paused: prev.ai?.settings?.isPaused || false }] }));
        alert('User not found in system. They have been added locally, and an email invite can be sent with the link.');
      }
    } else {
      // If we're not logged in, we can update local state
      setState(prev => ({ ...prev, collaborators: [...prev.collaborators, { id: Date.now(), ...collaborator, ai_paused: prev.ai?.settings?.isPaused || false }] }));
    }
  };

  const removeCollaborator = (id) => setState(prev => ({ ...prev, collaborators: prev.collaborators.filter(c => c.id !== id) }));
  const setStoryIdeas = (ideas) => setState(prev => ({ ...prev, storyIdeas: ideas }));

  const addToGallery = async (image) => {
    // Generate an ID for optimistic UI
    const tempId = Date.now();
    const imageWithUser = { ...image, user_id: user?.id };
    setState(prev => ({ ...prev, gallery: [{ ...imageWithUser, id: tempId }, ...(prev.gallery || [])] }));

    let currentStoryId = state.supabaseStoryId;
    if (!currentStoryId && user) {
      currentStoryId = await initializeStoryBackend();
    }

    if (currentStoryId && user) {
      try {
        const savedImage = await dbService.saveGalleryImage(currentStoryId, imageWithUser);
        // Replace temp ID with backend ID
        setState(prev => ({
          ...prev,
          gallery: (prev.gallery || []).map(img => img.id === tempId ? { ...img, id: savedImage.id } : img)
        }));
      } catch (err) {
        console.error("Failed to save gallery image to DB", err);
      }
    }
  };

  const restoreDeletedItem = async (logItem) => {
    try {
      const { type, item } = logItem;
      let restoredSuccessfully = false;

      if (type === 'story_entry' || type === 'archived_entry') {
         // Re-add as a normal story entry
         const restoredEntry = { ...item };
         delete restoredEntry.id; // remove old ID
         await addStoryEntry(restoredEntry);
         restoredSuccessfully = true;
      } else if (type === 'gallery_image') {
         const restoredImage = { ...item };
         delete restoredImage.id;
         await addToGallery(restoredImage);
         restoredSuccessfully = true;
      } else if (type === 'chapter_deleted' || type === 'chapter_clear') {
         // A bit more complex, we can restore it as an archived chapter
         const contentArray = Array.isArray(item) ? item : item.content;
         const title = item.title || 'Restored Chapter';
         const summary = item.summary || 'Restored from logs.';

         const newChapter = {
           id: Date.now(),
           title,
           content: contentArray || [],
           summary,
           timestamp: new Date().toISOString(),
           worldSnapshot: { ...state.world },
           wordCount: (contentArray || []).reduce((acc, e) => acc + (e.content ? e.content.split(/\s+/).length : 0), 0),
         };

         setState(prev => ({
           ...prev,
           archive: [newChapter, ...prev.archive],
           analytics: {
             ...prev.analytics,
             chaptersArchived: (prev.analytics.chaptersArchived || 0) + 1,
           }
         }));

         if (state.supabaseStoryId && user) {
           try {
             const savedChapter = await dbService.saveChapter(state.supabaseStoryId, newChapter);
             setState(prev => ({
               ...prev,
               archive: prev.archive.map(c => c.id === newChapter.id ? { ...c, id: savedChapter.id } : c)
             }));
           } catch (err) { console.error('Failed to save restored chapter', err); }
         }
         restoredSuccessfully = true;
      } else if (type === 'story_reset') {
         // Full story reset restoration
         await importStoryState(item);
         restoredSuccessfully = true;
      }

      // If successfully restored, remove it from the log
      if (restoredSuccessfully) {
        const logs = JSON.parse(localStorage.getItem('weaver_deletion_logs') || '[]');
        // We match by timestamp since that's unique enough for the log
        const filteredLogs = logs.filter(l => l.deletedAt !== logItem.deletedAt);
        localStorage.setItem('weaver_deletion_logs', JSON.stringify(filteredLogs));
      }

      return restoredSuccessfully;
    } catch (e) {
      console.error('Failed to restore item', e);
      return false;
    }
  };

  const updateGalleryImage = (id, newImageData) => {
    setState(prev => ({
      ...prev,
      gallery: (prev.gallery || []).map(img => img.id === id ? { ...img, ...newImageData } : img)
    }));

    if (state.supabaseStoryId && user) {
      dbService.updateGalleryImageDB(id, newImageData).catch(console.error);
    }
  };

  const deleteGalleryImage = (id) => {
    const imageToDelete = state.gallery?.find(img => img.id === id);
    if (imageToDelete) logDeletion('gallery_image', imageToDelete);

    setState(prev => ({
      ...prev,
      gallery: (prev.gallery || []).filter(img => img.id !== id)
    }));

    if (state.supabaseStoryId && user) {
      dbService.deleteGalleryImageDB(id).catch(console.error);
    }
  };

  const setSubscriptionTier = (tier) => setState(prev => ({ ...prev, subscriptionTier: tier }));

  return (
    <StoryContext.Provider value={{
      state,
      updateWorld,
      updateCharacter,
      addStoryEntry,
      setGenerating,
      setNarrativeStyle,
      addQuest,
      completeQuest,
      scheduleEvent,
      archiveCurrentStory,
      unarchiveChapter,
      editArchivedEntry,
      toggleArchivedEntryBookmark,
      toggleChapterBookmark,
      deleteArchivedEntry,
      loadStoryById,
      updateAI,
      updateAISettings,
      toggleAiPause,
      setSuggestions,
      addCollaborator,
      removeCollaborator,
      setStoryIdeas,
      addToGallery,
      updateGalleryImage,
      deleteGalleryImage,
      editStoryEntry,
      toggleEntryBookmark,
      deleteStoryEntry,
      startOverChapter,
      startOverStory,
      startCombat,
      endCombat,
      addCombatLog,
      importStoryState,
      restoreDeletedItem,
      setSubscriptionTier,
      initializeStoryBackend,
      submitPassage,
      updateStoryTitle,
      sendPoke,
    }}>
      {children}
    </StoryContext.Provider>
  );
};

export const useStory = () => useContext(StoryContext);